// src/hooks/menu/useInfiniteScroll.js
import { useEffect, useCallback, useRef } from 'react';
import { consoleDebug } from '../../util';
import { canDisplayMoreProducts, calculateRequiredDisplayFactor } from '../../utils/menu/productUtils';

export const useInfiniteScroll = ({
    isWideScreen,
    disableMobileInfiniteScroll,
    isFetchingNextPage,
    totalUniqueItemsFetched,
    paginationLimit,
    hasNextPage,
    fetchNextPage,
    displayFactorRef,
    setDisplayFactorRef
}) => {
    const loadMoreButtonRef = useRef(null);

    // Perform scroll actions
    const performScrollActions = useCallback(() => {
        if (!isWideScreen && disableMobileInfiniteScroll) return;
        
        const loadMoreButton = loadMoreButtonRef.current;
        if (!loadMoreButton) return;

        const { top } = loadMoreButton.getBoundingClientRect();
        const isButtonVisible = top - window.innerHeight <= 100;

        if (isButtonVisible && !isFetchingNextPage) {
            // Check if there are already fetched items not displayed yet
            if (canDisplayMoreProducts(totalUniqueItemsFetched, displayFactorRef.current, paginationLimit)) {
                consoleDebug('[InfiniteScroll] More items in cache to display. Incrementing display factor.');
                setDisplayFactorRef(displayFactorRef.current + 1);
            }
            // All fetched items are displayed, fetch more from API
            else if (hasNextPage) {
                consoleDebug('[InfiniteScroll] All cached items shown. Fetching next page from API.');
                fetchNextPage();
            }
        }
    }, [
        isWideScreen,
        disableMobileInfiniteScroll,
        isFetchingNextPage,
        totalUniqueItemsFetched,
        paginationLimit,
        hasNextPage,
        fetchNextPage,
        displayFactorRef,
        setDisplayFactorRef
    ]);

    // Handle scroll events
    useEffect(() => {
        if (typeof window === 'undefined') return;
        
        let debounceTimer = null;
        const debouncedScrollHandler = () => {
            clearTimeout(debounceTimer);
            debounceTimer = setTimeout(performScrollActions, 300);
        };
        
        window.addEventListener('scroll', debouncedScrollHandler);
        return () => {
            clearTimeout(debounceTimer);
            window.removeEventListener('scroll', debouncedScrollHandler);
        };
    }, [performScrollActions]);

    // Auto-increment display factor for newly fetched data
    useEffect(() => {
        if (totalUniqueItemsFetched > 0) {
            const currentMaxDisplayable = displayFactorRef.current * paginationLimit;
            
            if (currentMaxDisplayable < totalUniqueItemsFetched) {
                const requiredDisplayFactor = calculateRequiredDisplayFactor(totalUniqueItemsFetched, paginationLimit);
                
                if (displayFactorRef.current < requiredDisplayFactor) {
                    consoleDebug(`[InfiniteScroll] Auto-updating display factor from ${displayFactorRef.current} to ${requiredDisplayFactor}`);
                    setDisplayFactorRef(requiredDisplayFactor);
                }
            }
        }
    }, [totalUniqueItemsFetched, paginationLimit, displayFactorRef, setDisplayFactorRef]);

    // Handle load more button click
    const handleLoadMore = useCallback(() => {
        if (canDisplayMoreProducts(totalUniqueItemsFetched, displayFactorRef.current, paginationLimit)) {
            setDisplayFactorRef(displayFactorRef.current + 1);
        } else if (hasNextPage) {
            fetchNextPage();
        }
    }, [displayFactorRef, paginationLimit, totalUniqueItemsFetched, hasNextPage, fetchNextPage, setDisplayFactorRef]);

    return {
        loadMoreButtonRef,
        handleLoadMore
    };
};
